<template>
  <div class="m-edition-billing-artificial-intelligence-settings">
    <div v-if="cargando" class="ia-ajustes-cargando">
      <i class="fas fa-circle-notch fa-spin"></i> Cargando ajustes…
    </div>

    <template v-else>
      <!--
        Lo que se ofrece. Va primero y separado del resto porque es lo que se toca con apuro: si las
        imágenes salen caras un lunes, esto es lo que hay que encontrar sin leer nada.
      -->
      <section class="ia-ajustes-bloque">
        <header class="ia-ajustes-head">
          <div>
            <h3>Qué se ofrece</h3>
            <p>
              Se aplica en la siguiente petición, sin desplegar nada. Apagar algo no toca lo ya
              vendido: quien tiene créditos los conserva.
            </p>
          </div>
        </header>

        <div class="ia-ajustes-filas">
          <div
            v-for="fila in interruptores"
            :key="fila.clave"
            class="ia-ajuste-fila"
          >
            <button
              type="button"
              class="ia-ajuste-toggle"
              :class="{ encendido: valorDe(fila.clave) }"
              :disabled="guardando === fila.clave"
              @click="alternar(fila.clave)"
            >
              <i
                :class="guardando === fila.clave
                  ? 'fas fa-circle-notch fa-spin'
                  : (valorDe(fila.clave) ? 'fas fa-toggle-on' : 'fas fa-toggle-off')"
              ></i>
            </button>

            <div class="ia-ajuste-texto">
              <strong>
                {{ fila.titulo }}
                <em v-if="fila.caro" class="ia-ajuste-caro">LO MÁS CARO</em>
              </strong>
              <span>{{ fila.ayuda }}</span>
            </div>

            <AtomVolverAlServidor :origen="origenDe(fila.clave)" @volver="volverAlEnv(fila.clave)" />
          </div>
        </div>
      </section>

      <!--
        Qué modelo hace cada cosa. Es la pantalla principal de esta sección: cuatro decisiones, una
        por rol, y el proveedor sale del modelo elegido. Antes había que elegir primero un proveedor
        y después el modelo DE ESE proveedor, con lo cual los campos del otro quedaban ahí sin hacer
        nada y era fácil creer que estaban mandando.
      -->
      <section class="ia-ajustes-bloque">
        <header class="ia-ajustes-head">
          <div>
            <h3>Qué modelo usa cada cosa</h3>
            <p>
              Vale desde la próxima generación. Elegir el modelo alcanza: el proveedor sale de él, así
              que se puede tener las preguntas en uno y las imágenes en otro. Si un modelo no tiene
              precio cargado abajo, se cobra con el del <code>.env</code>.
            </p>
          </div>
        </header>

        <div class="ia-ajustes-campos">
          <div v-for="rol in roles" :key="rol.clave" class="ia-ajuste-campo">
            <label>
              <span>{{ rol.titulo }}</span>
              <AtomVolverAlServidor :origen="origenDe(rol.clave)" @volver="volverAlEnv(rol.clave)" />
            </label>

            <div class="ia-ajuste-campo-control">
              <select v-model="edicion[rol.clave]">
                <optgroup
                  v-for="grupo in modelosDe(rol.tipo)"
                  :key="grupo.proveedor"
                  :label="grupo.titulo"
                >
                  <option v-for="m in grupo.modelos" :key="m.modelo" :value="m.modelo">
                    {{ m.etiqueta }}
                  </option>
                </optgroup>
              </select>

              <button
                type="button"
                class="ia-ajuste-guardar"
                :disabled="guardando === rol.clave || !cambio(rol.clave) || vacio(rol.clave)"
                @click="guardar(rol.clave, edicion[rol.clave])"
              >
                <i :class="guardando === rol.clave ? 'fas fa-circle-notch fa-spin' : 'fas fa-check'"></i>
                Guardar
              </button>
            </div>
            <small>{{ rol.ayuda }} Hoy responde <strong>{{ proveedorDe(rol) }}</strong>.</small>
          </div>
        </div>
      </section>

      <!--
        Lo de abajo casi nunca se toca: forzar un proveedor a mano, el respaldo de Gemini cuando da
        503 y el modelo que usa cada proveedor si el de arriba no es suyo. Va plegado para que la
        pantalla no empiece con diez campos que no hay que tocar.
      -->
      <section class="ia-ajustes-bloque">
        <details>
          <summary class="ia-ajustes-avanzado">
            <i class="fas fa-sliders-h"></i> Ajustes avanzados de proveedor
          </summary>

        <div class="ia-ajustes-campos">
          <div v-for="campo in textos" :key="campo.clave" class="ia-ajuste-campo">
            <label>
              <span>{{ campo.titulo }}</span>
              <AtomVolverAlServidor :origen="origenDe(campo.clave)" @volver="volverAlEnv(campo.clave)" />
            </label>

            <div class="ia-ajuste-campo-control">
              <select v-if="campo.opciones" v-model="edicion[campo.clave]">
                <option v-for="opcion in campo.opciones" :key="opcion" :value="opcion">
                  {{ opcion }}
                </option>
              </select>
              <input v-else v-model="edicion[campo.clave]" type="text" maxlength="120" />

              <button
                type="button"
                class="ia-ajuste-guardar"
                :disabled="guardando === campo.clave || !cambio(campo.clave) || vacio(campo.clave)"
                @click="guardar(campo.clave, edicion[campo.clave])"
              >
                <i :class="guardando === campo.clave ? 'fas fa-circle-notch fa-spin' : 'fas fa-check'"></i>
                Guardar
              </button>
            </div>
            <small v-if="campo.ayuda">{{ campo.ayuda }}</small>
          </div>
        </div>
        </details>
      </section>

      <!--
        Las claves. No se muestran nunca —el backend no las devuelve— así que de una clave cargada
        solo se ve que existe y sus últimos cuatro caracteres, que alcanza para reconocerla.
      -->
      <section class="ia-ajustes-bloque">
        <header class="ia-ajustes-head">
          <div>
            <h3>Claves de los proveedores</h3>
            <p>
              Se guardan cifradas y no se pueden volver a ver: de una clave cargada solo se muestran
              sus últimos caracteres. Sin clave de Gemini, el asistente genera preguntas de prueba.
            </p>
          </div>
        </header>

        <div class="ia-ajustes-campos">
          <div v-for="secreto in secretos" :key="secreto.clave" class="ia-ajuste-campo">
            <label>
              <span>{{ secreto.titulo }}</span>
              <span class="ia-ajuste-estado" :class="{ cargada: estadoDe(secreto.clave).cargado }">
                <i :class="estadoDe(secreto.clave).cargado ? 'fas fa-key' : 'fas fa-unlock'"></i>
                {{ estadoDe(secreto.clave).cargado
                  ? `Cargada ${estadoDe(secreto.clave).pista || ''}`
                  : 'Sin cargar' }}
              </span>
            </label>

            <div class="ia-ajuste-campo-control">
              <input
                v-model="edicion[secreto.clave]"
                type="password"
                autocomplete="new-password"
                placeholder="Pegar una clave nueva para reemplazarla"
              />
              <button
                type="button"
                class="ia-ajuste-guardar"
                :disabled="guardando === secreto.clave || !edicion[secreto.clave]"
                @click="guardar(secreto.clave, edicion[secreto.clave])"
              >
                <i :class="guardando === secreto.clave ? 'fas fa-circle-notch fa-spin' : 'fas fa-check'"></i>
                Guardar
              </button>
              <button
                v-if="origenDe(secreto.clave) === 'base'"
                type="button"
                class="ia-ajuste-volver"
                @click="volverAlEnv(secreto.clave)"
              >
                <i class="fas fa-undo"></i> Usar la del servidor
              </button>
            </div>
          </div>
        </div>
      </section>

      <!--
        La tarifa. Es lo único de esta pantalla que decide cuánto se le cobra al cliente, así que va
        al final y con su aviso: un margen mal cargado se nota en la factura del mes siguiente.
      -->
      <section class="ia-ajustes-bloque">
        <header class="ia-ajustes-head">
          <div>
            <h3>Tarifa</h3>
            <p>
              Con esto se calcula cuántos créditos cuesta cada generación. Tocarlo cambia lo que se
              le descuenta a los clientes desde la próxima.
            </p>
          </div>
        </header>

        <div class="ia-ajustes-campos ia-ajustes-campos-numeros">
          <div v-for="numero in numeros" :key="numero.clave" class="ia-ajuste-campo">
            <label>
              <span>{{ numero.titulo }}</span>
              <AtomVolverAlServidor :origen="origenDe(numero.clave)" @volver="volverAlEnv(numero.clave)" />
            </label>
            <div class="ia-ajuste-campo-control">
              <input v-model="edicion[numero.clave]" type="number" :step="numero.paso || 1" min="0" />
              <button
                type="button"
                class="ia-ajuste-guardar"
                :disabled="guardando === numero.clave || !cambio(numero.clave) || vacio(numero.clave)"
                @click="guardar(numero.clave, edicion[numero.clave])"
              >
                <i :class="guardando === numero.clave ? 'fas fa-circle-notch fa-spin' : 'fas fa-check'"></i>
                Guardar
              </button>
            </div>
            <small v-if="numero.ayuda">{{ numero.ayuda }}</small>
          </div>
        </div>
      </section>
    </template>
  </div>
</template>

<script>
import AtomVolverAlServidor from '@/components/atoms/AtomVolverAlServidor'

/*
 | Lo que la pantalla ofrece, con la clave EXACTA que entiende el backend. El orden es el de la
 | pantalla, no el del backend: primero lo que se toca con apuro.
 |
 | No se arma leyendo la respuesta: el backend devuelve 23 ajustes y acá se eligen los que tienen
 | sentido tocar a mano, con un nombre que se entienda sin saber cómo se llama la variable.
 */
const INTERRUPTORES = [
  {
    clave: 'satelites.ia.visible_juedroop',
    titulo: 'Mostrar la IA en Juedroop',
    ayuda: 'El botón "Crear con IA", la pestaña de créditos y el resumen del menú.'
  },
  {
    clave: 'satelites.ia.visible_evaluaciones',
    titulo: 'Mostrar la IA en Evaluaciones',
    ayuda: 'Lo mismo en el Droop de Evaluaciones, por separado.'
  },
  {
    clave: 'satelites.ia.visible_academy',
    titulo: 'Vender IA desde el panel de la academia',
    ayuda: 'Los planes mensuales y los paquetes de créditos como satélites de un producto.'
  },
  {
    clave: 'evaluacion.ia.habilitada',
    titulo: 'Asistente de IA en Evaluaciones',
    ayuda: 'Crear una evaluación con IA y los botones de IA del editor. Apagado, no se ofrecen y esas rutas no existen.'
  },
  {
    clave: 'ia.imagen.habilitada',
    titulo: 'Portada del juego con IA',
    ayuda: 'Una sola ilustración por juego, opcional para el profesor.'
  },
  {
    clave: 'ia.imagen.preguntas_habilitada',
    titulo: 'Una imagen para cada pregunta',
    ayuda: 'Una por pregunta: un juego de 10 cuesta unas treinta veces el texto.',
    caro: true
  },
  {
    clave: 'satelites.ia.cobro_habilitado',
    titulo: 'Descontar créditos',
    ayuda: 'Apagado, la IA genera igual y no se le cobra a nadie.'
  },
  {
    clave: 'satelites.ia.registro_habilitado',
    titulo: 'Registrar cada generación',
    ayuda: 'Lo que alimenta "Consumo de IA": costo, tokens, modelo y duración.'
  },
  {
    clave: 'satelites.ia.registro_contexto',
    titulo: 'Guardar el material del cliente',
    ayuda: 'Además, el texto que escribió y los enunciados generados. Es material de las academias.'
  }
]

/*
 | Las cuatro decisiones que de verdad se toman: con qué se escriben las preguntas y con qué se
 | dibujan las imágenes, cada una en su calidad. El proveedor sale del modelo elegido, así que esto
 | es todo lo que hay que tocar para cambiar de proveedor.
 */
const ROLES = [
  {
    clave: 'ia.texto.modelo',
    tipo: 'texto',
    titulo: 'Preguntas · económico',
    ayuda: 'El de todos los días.'
  },
  {
    clave: 'ia.texto.modelo_alta',
    tipo: 'texto',
    titulo: 'Preguntas · calidad extra',
    ayuda: 'El que se usa cuando el profesor pide calidad extra.'
  },
  {
    clave: 'ia.imagen.modelo',
    tipo: 'imagen',
    titulo: 'Imágenes · económica',
    ayuda: 'La portada y, si se piden, las de cada pregunta.'
  },
  {
    clave: 'ia.imagen.modelo_alta',
    tipo: 'imagen',
    titulo: 'Imágenes · calidad extra',
    ayuda: 'Lo mismo, con el modelo caro.'
  }
]

/** Cómo se llama cada proveedor en los desplegables. */
const PROVEEDORES = {
  gemini: 'Google Gemini',
  openai: 'OpenAI',
  '': 'Cargado a mano'
}

const TEXTOS = [
  {
    clave: 'ia.proveedor',
    titulo: 'Forzar el proveedor de las preguntas',
    opciones: ['auto', 'gemini', 'openai', 'local'],
    ayuda: 'Con "auto" manda el modelo elegido arriba. Sin ninguna clave cargada, el simulador.'
  },
  {
    clave: 'ia.proveedor_preferido',
    titulo: 'Preferido si hay varias claves',
    opciones: ['gemini', 'openai']
  },
  {
    clave: 'ia.imagen.proveedor',
    titulo: 'Forzar el proveedor de las imágenes',
    opciones: ['auto', 'gemini', 'openai', 'local'],
    ayuda: 'Con "auto" manda el modelo elegido arriba, que es lo normal.'
  },
  { clave: 'ia.gemini.modelo', titulo: 'Gemini · modelo si no se eligió uno suyo arriba' },
  { clave: 'ia.gemini.modelo_alta', titulo: 'Gemini · ídem, calidad extra' },
  { clave: 'ia.gemini.modelo_respaldo', titulo: 'Gemini · respaldo (si da 503)' },
  { clave: 'ia.gemini.modelo_respaldo_alta', titulo: 'Gemini · respaldo de calidad extra' },
  { clave: 'ia.openai.modelo', titulo: 'OpenAI · modelo si no se eligió uno suyo arriba' },
  { clave: 'ia.openai.modelo_alta', titulo: 'OpenAI · ídem, calidad extra' }
]

const SECRETOS = [
  { clave: 'ia.gemini.api_key', titulo: 'Clave de Gemini (Google AI Studio)' },
  { clave: 'ia.openai.api_key', titulo: 'Clave de OpenAI' }
]

const NUMEROS = [
  {
    clave: 'ia.creditos.margen',
    titulo: 'Margen',
    paso: 0.1,
    ayuda: 'Se le cobra al cliente este múltiplo de lo que cuesta el proveedor.'
  },
  {
    clave: 'ia.creditos.creditos_por_usd',
    titulo: 'Créditos por dólar',
    ayuda: 'El valor de respaldo: cada licencia congela el suyo según el plan que compró.'
  },
  { clave: 'ia.creditos.minimo_creditos', titulo: 'Mínimo por generación' },
  { clave: 'ia.creditos.maximo_creditos', titulo: 'Máximo por generación' }
]

export default {
  name: 'MoleculeEditionBillingArtificialIntelligenceSettings',

  components: { AtomVolverAlServidor },

  data () {
    return {
      ajustes: {},
      cargando: true,
      // La clave que se está guardando, para no dejar apretar dos veces
      guardando: '',
      // Lo que se está escribiendo, por clave
      edicion: {},
      interruptores: INTERRUPTORES,
      roles: ROLES,
      /** Los modelos que ofrece el backend, por tipo. Vacío hasta la primera lectura. */
      modelos: { texto: [], imagen: [] },
      textos: TEXTOS,
      secretos: SECRETOS,
      numeros: NUMEROS
    }
  },

  created () {
    this.leer()
  },

  methods: {
    leer () {
      this.cargando = true

      this.$services.read('sateliteIaMonitoreo', 'readMonitoreoIaAjustes')
        .then(({ data }) => {
          this.ajustes = {}
          ;(data.ajustes || []).forEach(ajuste => {
            this.$set(this.ajustes, ajuste.clave, ajuste)
          })
          this.modelos = {
            texto: (data.modelos && data.modelos.texto) || [],
            imagen: (data.modelos && data.modelos.imagen) || []
          }
          this.sincronizarEdicion()
        })
        .catch(() => {
          this.$alerts.pushAlertNotTranslated('No se pudieron leer los ajustes de IA.', 'a-alert-danger')
        })
        .finally(() => {
          this.cargando = false
        })
    },

    /*
     | Lo editable arranca con lo que vale hoy, no vacío: así el campo muestra el modelo que se está
     | usando y el botón de guardar solo se enciende cuando de verdad se cambió algo.
     */
    sincronizarEdicion () {
      const edicion = {}

      TEXTOS.concat(NUMEROS).concat(ROLES).forEach(campo => {
        const ajuste = this.ajustes[campo.clave]
        edicion[campo.clave] = ajuste && ajuste.valor !== null ? ajuste.valor : ''
      })
      SECRETOS.forEach(secreto => {
        edicion[secreto.clave] = ''
      })

      this.edicion = edicion
    },

    /** Los modelos de un tipo, agrupados por proveedor para los <optgroup>. */
    modelosDe (tipo) {
      const grupos = []

      ;(this.modelos[tipo] || []).forEach(modelo => {
        let grupo = grupos.find(g => g.proveedor === modelo.proveedor)

        if (!grupo) {
          grupo = {
            proveedor: modelo.proveedor,
            titulo: PROVEEDORES[modelo.proveedor] || modelo.proveedor,
            modelos: []
          }
          grupos.push(grupo)
        }

        grupo.modelos.push(modelo)
      })

      return grupos
    },

    /*
     | Quién va a responder con lo que está elegido ahora. Se muestra abajo de cada desplegable
     | porque es la mitad de la información: elegir un modelo es elegir un proveedor, y conviene
     | verlo sin tener que saberse de memoria de quién es cada nombre.
     */
    proveedorDe (rol) {
      const elegido = this.edicion[rol.clave]
      const modelo = (this.modelos[rol.tipo] || []).find(m => m.modelo === elegido)

      if (!modelo || !modelo.proveedor) {
        return 'el proveedor configurado abajo'
      }

      return PROVEEDORES[modelo.proveedor] || modelo.proveedor
    },

    valorDe (clave) {
      const ajuste = this.ajustes[clave]
      return !!(ajuste && ajuste.valor)
    },

    origenDe (clave) {
      const ajuste = this.ajustes[clave]
      return ajuste ? ajuste.origen : 'env'
    },

    estadoDe (clave) {
      return this.ajustes[clave] || { cargado: false, pista: null }
    },

    /*
     | Un campo vaciado no se guarda: el backend rechaza el vacío a propósito (guardar '' en la
     | clave de un proveedor dejaría al asistente sin proveedor). Lo que se quiere al vaciar un
     | campo es volver al valor del servidor, y eso es borrar la fila, no guardar nada.
     */
    vacio (clave) {
      const escrito = this.edicion[clave]
      return escrito === undefined || escrito === null || String(escrito).trim() === ''
    },

    /** Si lo escrito difiere de lo vigente: sin cambio no hay nada que guardar. */
    cambio (clave) {
      const ajuste = this.ajustes[clave]
      const actual = ajuste && ajuste.valor !== null ? String(ajuste.valor) : ''
      return String(this.edicion[clave] === undefined ? '' : this.edicion[clave]) !== actual
    },

    alternar (clave) {
      this.guardar(clave, !this.valorDe(clave))
    },

    guardar (clave, valor) {
      if (this.guardando) return

      this.guardando = clave

      /*
       | El valor viaja como TEXTO, nunca como booleano: el backend recorta cada entrada de la
       | petición y un `false` del JSON le llega vacío, que leía como "falta el valor" en vez de
       | "apagado". Mandando 'true'/'false' el PUT dice lo que quiere decir.
       */
      const texto = typeof valor === 'boolean' ? String(valor) : valor

      this.$services.update('sateliteIaMonitoreo', 'updateMonitoreoIaAjuste', { clave, valor: texto })
        .then(() => {
          this.$alerts.pushAlertNotTranslated('Ajuste guardado. Vale desde la próxima petición.', 'a-alert-success')
          this.leer()
        })
        .catch(error => {
          this.$alerts.pushAlertNotTranslated(this.mensajeDeError(error), 'a-alert-danger')
        })
        .finally(() => {
          this.guardando = ''
        })
    },

    /** Vuelve al valor del servidor: borra la fila en vez de guardar un vacío. */
    volverAlEnv (clave) {
      if (this.guardando) return

      this.guardando = clave

      this.$services.update('sateliteIaMonitoreo', 'updateMonitoreoIaAjuste', { clave, borrar: true })
        .then(() => {
          this.$alerts.pushAlertNotTranslated('Vuelve a valer lo que diga el servidor.', 'a-alert-success')
          this.leer()
        })
        .catch(error => {
          this.$alerts.pushAlertNotTranslated(this.mensajeDeError(error), 'a-alert-danger')
        })
        .finally(() => {
          this.guardando = ''
        })
    },

    /*
     | Los errores de negocio del core llegan con {error:{message}} y el status no es fiable: hay que
     | leer el mensaje, que ya viene escrito para que lo lea una persona.
     */
    mensajeDeError (error) {
      const mensaje = error && error.response && error.response.data &&
        error.response.data.error && error.response.data.error.message

      return mensaje || 'No se pudo guardar el ajuste.'
    }
  }
}
</script>
